import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GlobalAudioService } from '@/services/audio/GlobalAudioService';
import { useAudioContext } from '@/contexts/AudioContext';
import { audioPlaylistService } from '@/services/audio/AudioPlaylistService';
import { audioPositionTracker } from '@/services/audio/AudioPositionTracker';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  SkipBack, 
  SkipForward,
  X,
  Maximize2,
  Music
} from 'lucide-react';
import { generateModuleRoute } from '@/utils/routeUtils';
import { determineCourseType } from '@/utils/routeUtils';
import { useToast } from '@/hooks/use-toast';

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const MiniAudioPlayer = () => {
  const { audioState, isAudioActive } = useAudioContext();
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [playlistInfo, setPlaylistInfo] = useState<{ currentIndex: number; total: number }>({ currentIndex: -1, total: 0 });
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [savedPosition, setSavedPosition] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // PHASE 3: Route-aware visibility - only show on module pages
  const isOnModulePage = /\/[^/]+-courses\/[^/]+\/[^/]+$/.test(location.pathname);

  // Sync volume with global service
  useEffect(() => {
    setVolume(audioState.volume);
  }, [audioState.volume]);

  // Subscribe to playlist updates
  useEffect(() => {
    const unsubscribe = audioPlaylistService.subscribe((playlist, currentIndex) => {
      setPlaylistInfo({ currentIndex, total: playlist.length });
    });
    return unsubscribe;
  }, []);

  // PHASE 3 & 4: Check for saved position with bulletproof null safety and error handling
  useEffect(() => {
    // Early return if no valid audio context
    if (!audioState?.moduleId || !audioState?.courseId || audioState?.isPlaying) {
      return;
    }

    // PHASE 3: Wrap in try-catch to prevent any errors from bubbling up
    try {
      const saved = audioPositionTracker.getSavedPosition(audioState.moduleId, audioState.courseId);
      
      if (saved && saved.position > 5 && saved.position < saved.duration - 10) {
        setSavedPosition(saved.position);
        setShowResumePrompt(true);
      } else {
        setShowResumePrompt(false);
        setSavedPosition(null);
      }
    } catch (error) {
      // PHASE 3: Silently handle errors - audio position tracking is non-critical
      console.warn('[MiniAudioPlayer] Position check failed (non-critical):', error);
      setShowResumePrompt(false);
      setSavedPosition(null);
    }
  }, [audioState?.moduleId, audioState?.courseId, audioState?.isPlaying]);

  // PHASE 3: Bulletproof play/pause with error handling
  const handlePlayPause = () => {
    try {
      if (audioState.isPlaying && !audioState.isPaused) {
        GlobalAudioService.pause();
      } else {
        GlobalAudioService.play();
      }
    } catch (error) {
      console.warn('[MiniAudioPlayer] Play/pause failed (non-critical):', error);
      toast({
        title: "Audio playback issue",
        description: "Please try again in a moment.",
        variant: "destructive",
        duration: 3000
      });
    }
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    GlobalAudioService.setVolume(newVolume);
  };

  const handleToggleMute = () => {
    if (isMuted) {
      const newVolume = 0.8;
      setVolume(newVolume);
      setIsMuted(false);
      GlobalAudioService.setVolume(newVolume);
    } else {
      setIsMuted(true);
      GlobalAudioService.setVolume(0);
    }
  };

  const handleSeek = (value: number[]) => {
    GlobalAudioService.seekTo(value[0]);
  };

  // PHASE 3 & 4: Safe navigation with error handling
  const handleNavigateToModule = () => {
    try {
      if (audioState?.moduleId && audioState?.courseId) {
        const courseType = determineCourseType(audioState.courseId);
        const route = generateModuleRoute(audioState.courseId, audioState.moduleId, courseType);
        navigate(route);
      }
    } catch (error) {
      console.warn('[MiniAudioPlayer] Navigation failed (non-critical):', error);
      toast({
        title: "Navigation issue",
        description: "Unable to navigate to module.",
        variant: "destructive",
        duration: 3000
      });
    }
  };

  const handleClose = () => {
    GlobalAudioService.stop();
    audioPlaylistService.clearPlaylist();
  };

  const handleResume = () => {
    if (savedPosition) {
      GlobalAudioService.seekTo(savedPosition);
      GlobalAudioService.play();
      setShowResumePrompt(false);
      toast({
        title: "▶️ Resuming from saved position",
        description: `Starting at ${formatTime(savedPosition)}`,
        duration: 2000
      });
    }
  };

  const handleStartFromBeginning = () => {
    GlobalAudioService.seekTo(0);
    GlobalAudioService.play();
    setShowResumePrompt(false);
    if (audioState.moduleId && audioState.courseId) {
      audioPositionTracker.clearPosition(audioState.moduleId, audioState.courseId);
    }
  };

  // PHASE 3 & 4: Don't render if not on module page or audio isn't properly initialized
  if (!isAudioActive || !audioState.moduleId || !audioState.courseId || !isOnModulePage) {
    return null;
  }

  return (
    <Card className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t shadow-lg">
      <div className="container mx-auto px-4 py-3">
        {/* Resume Prompt Banner */}
        {showResumePrompt && savedPosition && (
          <div className="mb-3 p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1">
              <Music className="h-4 w-4 text-primary" />
              <div>
                <p className="text-sm font-medium">Resume from {formatTime(savedPosition)}?</p>
                <p className="text-xs text-muted-foreground">You left off here last time</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleStartFromBeginning}>
                Start Over
              </Button>
              <Button size="sm" onClick={handleResume}>
                Resume ▶️
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center gap-4">
          {/* Module Info with Playlist Position */}
          <div className="flex-1 min-w-0">
            <button
              onClick={handleNavigateToModule}
              className="text-left w-full hover:text-primary transition-colors"
            >
              <div className="flex items-center gap-2">
                <Music className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {audioState.moduleTitle || 'Playing Audio'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {audioState.courseTitle && (
                      <span className="truncate">{audioState.courseTitle}</span>
                    )}
                    {playlistInfo.total > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {playlistInfo.currentIndex + 1} / {playlistInfo.total}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Playback Controls */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => GlobalAudioService.skipBackward(30)}
              className="h-8 w-8 p-0"
              title="Rewind 30s"
            >
              <SkipBack className="h-4 w-4" />
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={handlePlayPause}
              className="h-10 w-10 p-0 rounded-full"
              title={audioState.isPlaying && !audioState.isPaused ? 'Pause' : 'Play'}
            >
              {audioState.isPlaying && !audioState.isPaused ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="h-5 w-5 ml-0.5" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => GlobalAudioService.skipForward(30)}
              className="h-8 w-8 p-0"
              title="Forward 30s"
            >
              <SkipForward className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress Bar & Time */}
          <div className="hidden md:flex flex-col flex-1 min-w-[200px] max-w-[400px]">
            <Slider
              value={[audioState.currentTime]}
              onValueChange={handleSeek}
              max={audioState.duration || 100}
              step={0.1}
              className="w-full"
              aria-label="Audio progress"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>{formatTime(audioState.currentTime)}</span>
              <span>{formatTime(audioState.duration)}</span>
            </div>
          </div>

          {/* Volume Control */}
          <div className="hidden lg:flex items-center gap-2 min-w-[120px]">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleMute}
              className="h-8 w-8 p-0"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
            </Button>
            <Slider
              value={[volume]}
              onValueChange={handleVolumeChange}
              max={1}
              step={0.01}
              className="w-full"
              aria-label="Volume control"
            />
          </div>

          {/* Go to Module Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNavigateToModule}
            className="h-8 px-3 hidden sm:flex"
            title="Go to module"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>

          {/* Close Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-8 w-8 p-0"
            title="Stop audio"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
